// ./src/components/features/CreateEventWizard/steps/AccommodationsStep.tsx
//
// "Where can out-of-town guests stay?" — shown when the Accommodations feature is on (ROADMAP A2b #62 P6(f)). Each stay is
// made in the Accommodations page's own `AccommodationEditDrawer` (price, group booking code, photos), under a provider
// scoped by the event — the drawer saves THROUGH that provider and does nothing without it.
// ⓘ The drawer suggests each stay's distance from the venue, so it gets the venue's coordinates exactly as the page does
//   (`useVenueCoordinates`). Before the Event page step, which offers the "Where to stay" block once a stay exists.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { AccommodationsProvider, useAccommodationsContext } from '@/contexts';
import { useEvent, useToast, useVenueCoordinates } from '@/hooks';
import { Accommodation, WHOLE_SET } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { AccommodationEditDrawer } from '@/components/features/Accommodations';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

/** The host's order, then by name — as on the Accommodations page. */
const byOrderThenName = (a: Accommodation, b: Accommodation): number =>
  (a.orderNo ?? 0) - (b.orderNo ?? 0) || (a.name ?? '').localeCompare(b.name ?? '');

const AccommodationsStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { accommodations, list, isLoaded, remove } = useAccommodationsContext();
  // The SAVED event's venue, not the draft's — a restored draft can hold a venue changed since (review, 2026-10-01).
  // ⓘ Best-effort, like the page: until it is read the drawer just suggests no distance.
  const { event } = useEvent(eventId);
  const venue = useVenueCoordinates(event?.venueId);

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<Accommodation | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => [...accommodations].sort(byOrderThenName), [accommodations]);

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.accommodations.intro', {
          defaultValue:
            'Suggest places for out-of-town guests to stay — with the price and any group booking code. The Event page step offers a “Where to stay” block to show them.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.accommodations.title', { defaultValue: 'Accommodations' })}
        addLabel={t('events:wizard.accommodations.add', { defaultValue: 'Add a place to stay' })}
        onAdd={() => setDrawer({ recordId: null })}
        gate={null}
        gateWhat=""
        disabled={saving}
        isLoaded={isLoaded || !!readError}
        readError={readError}
        onRetry={() => void load()}
        errorTitle={t('events:wizard.accommodations.loadFailed', { defaultValue: 'Could not load the accommodations' })}
        operation="accommodationList"
        emptyText={t('events:wizard.accommodations.none', { defaultValue: 'No places to stay yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((a) => (
          <WizardItemRow
            key={a.id}
            icon="Bed"
            title={a.name ?? ''}
            subtitle={a.streetAddress || null}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: a.id as string })}
            onRemove={() => setPendingRemove(a)}
          />
        ))}
      </WizardListSection>

      <AccommodationEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        eventId={eventId}
        venueLatitude={venue.latitude}
        venueLongitude={venue.longitude}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.accommodations.removeTitle', { defaultValue: 'Remove this place to stay?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const AccommodationsStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <AccommodationsProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <AccommodationsStepBody eventId={createdEventId} />
    </AccommodationsProvider>
  );
};
